import { useState } from 'react';

// Preisberechnung basierend auf der neuen Python-Formel (langsamer fallend)
const MIN_PREIS = 1.90;
const MAX_PREIS = 3.00;
const ALPHA = 2.0;
const K = 800.0;
const PREIS_PRO_STELLPLATZ = 20.00;

function roundToNearest005(value: number): number {
  return Math.round(value / 0.05) * 0.05;
}

function preisProPaket(x: number): number {
  if (x < 1) return MAX_PREIS;
  
  // Neue Formel: Preis(x) = min + (max - min) * (1 + (x-1)/k)^(-alpha)
  const factor = Math.pow(1.0 + (x - 1.0) / K, -ALPHA);
  let rawPrice = MIN_PREIS + (MAX_PREIS - MIN_PREIS) * factor;
  
  // Numerische Begrenzung
  rawPrice = Math.max(MIN_PREIS, Math.min(rawPrice, MAX_PREIS));
  
  // Rundung auf 0,05-Schritte
  const rounded = roundToNearest005(rawPrice);
  
  return parseFloat(rounded.toFixed(2));
}

export default function PriceCalculator() {
  const [packages, setPackages] = useState(10);
  const [pallets, setPallets] = useState(0);

  // Progressive scale: maps slider position (0-100) to package count (0-5000)
  const sliderToPackages = (value: number): number => {
    if (value === 0) return 0;
    if (value <= 50) {
      // First half: 0-50 slider = 0-500 packages (linear)
      return Math.round((value / 50) * 500);
    } else {
      // Second half: 50-100 slider = 500-5000 packages (linear)
      return Math.round(500 + ((value - 50) / 50) * 4500);
    }
  };

  // Reverse: maps package count to slider position
  const packagesToSlider = (packages: number): number => {
    if (packages === 0) return 0;
    if (packages <= 500) {
      // 0-500 packages = 0-50 slider
      return Math.round((packages / 500) * 50);
    } else {
      // 500-5000 packages = 50-100 slider
      return Math.round(50 + ((packages - 500) / 4500) * 50);
    }
  };

  // Progressive scale for pallets: maps slider position (0-100) to pallet count (0-100)
  const sliderToPallets = (value: number): number => {
    if (value === 0) return 0;
    if (value <= 50) {
      // First half: 0-50 slider = 0-20 pallets (linear)
      return Math.round((value / 50) * 20);
    } else {
      // Second half: 50-100 slider = 20-100 pallets (linear)
      return Math.round(20 + ((value - 50) / 50) * 80);
    }
  };

  // Reverse: maps pallet count to slider position
  const palletsToSlider = (pallets: number): number => {
    if (pallets === 0) return 0;
    if (pallets <= 20) {
      // 0-20 pallets = 0-50 slider
      return Math.round((pallets / 20) * 50);
    } else {
      // 20-100 pallets = 50-100 slider
      return Math.round(50 + ((pallets - 20) / 80) * 50);
    }
  };

  const packageSliderValue = packagesToSlider(packages);
  const palletSliderValue = palletsToSlider(pallets);
  const pricePerPackage = packages > 0 ? preisProPaket(packages) : MAX_PREIS;
  const packagesTotalPrice = packages * pricePerPackage;
  const palletsTotalPrice = pallets * PREIS_PRO_STELLPLATZ;
  const totalPrice = packagesTotalPrice + palletsTotalPrice;

  const handlePackageSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPackages = sliderToPackages(Number(e.target.value));
    setPackages(newPackages);
  };

  const handlePalletSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPallets = sliderToPallets(Number(e.target.value));
    setPallets(newPallets);
  };

  const handlePackageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Leere Eingabe erlauben
    if (value === '') {
      setPackages(0);
      return;
    }
    
    const numValue = Number(value);
    
    // Nur gültige Zahlen zwischen 0 und 5000 akzeptieren
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 5000) {
      setPackages(Math.round(numValue));
    }
  };

  const handlePalletInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Leere Eingabe erlauben
    if (value === '') {
      setPallets(0);
      return;
    }
    
    const numValue = Number(value);
    
    // Nur gültige Zahlen zwischen 0 und 100 akzeptieren
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setPallets(Math.round(numValue));
    }
  };

  const handlePackageInputBlur = () => {
    // Wenn das Feld leer ist, setze auf 0
    if (packages === 0 || isNaN(packages)) {
      setPackages(0);
    }
  };

  const handlePalletInputBlur = () => {
    // Wenn das Feld leer ist, setze auf 0
    if (pallets === 0 || isNaN(pallets)) {
      setPallets(0);
    }
  };

  return (
    <section id="price-calculator" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Preiskalkulator
            </h2>
            <p className="text-xl text-gray-600">
              Berechnen Sie sofort, was unser Service für Sie kostet. Stellen Sie einfach die Anzahl der Pakete und Palettenstellplätze pro Monat ein!
            </p>
          </div>

          <div className="bg-white p-8 md:p-12 rounded-xl shadow-lg border border-gray-200">
            {/* Packages Input */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4 gap-4">
                <label className="text-lg font-semibold text-black break-words hyphens-auto" style={{wordBreak: 'break-word', hyphens: 'auto'}}>
                  Anzahl Pakete pro Monat
                </label>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <input
                    type="number"
                    min="0"
                    max="5000"
                    value={packages}
                    onChange={handlePackageInputChange}
                    onBlur={handlePackageInputBlur}
                    className="w-24 px-4 py-2 text-lg font-bold text-black border-2 border-gray-300 rounded-lg text-center focus:outline-none focus:border-black cursor-text"
                  />
                </div>
              </div>

              {/* Slider */}
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={packageSliderValue}
                  onChange={handlePackageSliderChange}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #B80E10 0%, #B80E10 ${packageSliderValue}%, #e5e7eb ${packageSliderValue}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-2 relative">
                  <span className="absolute" style={{left: '0%', transform: 'translateX(-50%)'}}>0</span>
                  <span className="absolute" style={{left: '25%', transform: 'translateX(-50%)'}}>250</span>
                  <span className="absolute" style={{left: '50%', transform: 'translateX(-50%)'}}>500</span>
                  <span className="absolute" style={{left: '75%', transform: 'translateX(-50%)'}}>2.750</span>
                  <span className="absolute" style={{left: '100%', transform: 'translateX(-50%)'}}>5.000</span>
                </div>
              </div>
            </div>

            {/* Pallets Input */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4 gap-4">
                <label className="text-lg font-semibold text-black break-words hyphens-auto" style={{wordBreak: 'break-word', hyphens: 'auto'}}>
                  Anzahl Palettenstellplätze pro Monat
                </label>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={pallets}
                    onChange={handlePalletInputChange}
                    onBlur={handlePalletInputBlur}
                    className="w-24 px-4 py-2 text-lg font-bold text-black border-2 border-gray-300 rounded-lg text-center focus:outline-none focus:border-black cursor-text"
                  />
                </div>
              </div>

              {/* Slider */}
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={palletSliderValue}
                  onChange={handlePalletSliderChange}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #B80E10 0%, #B80E10 ${palletSliderValue}%, #e5e7eb ${palletSliderValue}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-2 relative">
                  <span className="absolute" style={{left: '0%', transform: 'translateX(-50%)'}}>0</span>
                  <span className="absolute" style={{left: '25%', transform: 'translateX(-50%)'}}>10</span>
                  <span className="absolute" style={{left: '50%', transform: 'translateX(-50%)'}}>20</span>
                  <span className="absolute" style={{left: '75%', transform: 'translateX(-50%)'}}>60</span>
                  <span className="absolute" style={{left: '100%', transform: 'translateX(-50%)'}}>100</span>
                </div>
              </div>
            </div>

            {/* Price Display */}
            <div className="space-y-6 mb-8">
              {/* Einzelpreise */}
              <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
                <h4 className="text-lg font-bold text-black mb-4">Einzelpreise</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-700 text-sm md:text-base flex-shrink-0">Preis pro Paket:</span>
                    <span className="text-lg font-bold text-black whitespace-nowrap text-right">
                      {pricePerPackage.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-700 text-sm md:text-base flex-shrink">Preis pro Palettenstellplatz:</span>
                    <span className="text-lg font-bold text-black whitespace-nowrap text-right flex-shrink-0">
                      {PREIS_PRO_STELLPLATZ.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Gesamtpreise */}
              <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
                <h4 className="text-lg font-bold text-black mb-4">Gesamtpreise</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-700 text-sm md:text-base">Preis Pakete gesamt:</span>
                    <span className="text-lg font-bold text-black whitespace-nowrap text-right">
                      {packagesTotalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-700 text-sm md:text-base flex-shrink break-words hyphens-auto" style={{wordBreak: 'break-word', hyphens: 'auto'}}>Preis Palettenstellplätze gesamt:</span>
                    <span className="text-lg font-bold text-black whitespace-nowrap text-right flex-shrink-0">
                      {palletsTotalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-base md:text-lg font-semibold text-black flex-shrink break-words hyphens-auto" style={{wordBreak: 'break-word', hyphens: 'auto'}}>Gesamtpreis pro Monat:</span>
                      <span className="text-xl md:text-2xl font-bold whitespace-nowrap text-right flex-shrink-0" style={{color: '#B80E10'}}>
                        {totalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Included Services */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-black mb-4 flex items-center">
                  <i className="ri-check-double-line text-xl mr-2" style={{color: '#B80E10'}}></i>
                  Im Preis enthalten:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-black mr-3 mt-1"></i>
                    <span>Handverpackung mit Liebe</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-black mr-3 mt-1"></i>
                    <span>Qualitätskontrolle</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-black mr-3 mt-1"></i>
                    <span>Verpackungsmaterial</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-black mr-3 mt-1"></i>
                    <span>1 Produkt (bis 1 kg)</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-black mr-3 mt-1"></i>
                    <span>1 Dokument</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-black mb-4 flex items-center">
                  <i className="ri-information-line text-xl mr-2" style={{color: '#B80E10'}}></i>
                  Zusätzliche Infos:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-arrow-right-s-line text-black mr-3 mt-1"></i>
                    <span>Keine Mindestlaufzeit</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-arrow-right-s-line text-black mr-3 mt-1"></i>
                    <span>Flexible Anpassung</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-arrow-right-s-line text-black mr-3 mt-1"></i>
                    <span>schon ab 1 Paket</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-8 text-center">
              <a
                href="/test-buchen"
                className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                style={{backgroundColor: '#B80E10'}}
              >
                Test buchen
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
